/* The two writes a lead record can make, and the refusal that matters most.
 *
 * THE PRIMARY ACTION IS THE STAGE'S NEXT MOVE, and it is the same move the cadence
 * strip beside it names. "Add note" was the primary on every record in the shipped
 * demo, so a lead with both trials done offered the same first button as one nobody
 * had called.
 *
 * A LEAD CANNOT BE CLOSED AS LOST WITHOUT A REASON. The migration found 57 closed
 * with the bare word "Others" and 351 more with the real reason typed into a column
 * nobody could report on. The reasons are a list because a list can be counted, and
 * "Something else" asks for the words rather than swallowing them.
 */
import { useState } from 'react';
import type { Lead, LeadStage } from '@acrostic/model';
import { DROPOUT_REASONS } from '@acrostic/model';
import { useAsync, useSource } from '../../data/SourceProvider.js';
import { Field, Modal } from '../../components/Modal.js';
import { useToast } from '../../components/Toast.js';
import { OPEN_PATH, STAGES } from '../../lib/leads.js';

const FREETEXT = '__something_else__';

/** What this stage owes next, and which move it is. */
export const PRIMARY: Partial<Record<LeadStage, { label: string; kind: 'trial' | 'stage' | 'note'; to?: LeadStage }>> = {
  new: { label: 'Book their first trial', kind: 'trial' },
  contacted: { label: 'Book their first trial', kind: 'trial' },
  prospect_1: { label: 'Book the second trial', kind: 'trial' },
  prospect_2: { label: 'Close: membership', kind: 'stage', to: 'won' },
  nurture: { label: 'Reconnect', kind: 'note' },
};

export function StageModal({ l, to, onClose, onDone }: {
  l: Lead; to: LeadStage | null; onClose: () => void; onDone: () => void;
}) {
  const source = useSource();
  const toast = useToast();
  const [stage, setStage] = useState<LeadStage>(to ?? l.stage);
  const [reason, setReason] = useState<string>(DROPOUT_REASONS[0]);
  const [detail, setDetail] = useState('');
  const [busy, setBusy] = useState(false);
  const lost = stage === 'lost';
  const free = reason === FREETEXT;

  async function save() {
    setBusy(true);
    try {
      await source.setLeadStage(l.id, stage, lost
        ? { reason: free ? detail.trim() : reason, detail: free ? '' : detail.trim() }
        : undefined);
      toast(`${l.name} → ${STAGES[stage].short}`, 'good');
      onDone();
    } catch (e) {
      toast((e as Error).message, 'bad');
      setBusy(false);
    }
  }

  return (
    <Modal
      title={'Move stage · ' + l.name} onClose={onClose}
      footer={
        <>
          <button type="button" className="btn t-label-medium" onClick={onClose}>Cancel</button>
          <button
            type="button" className="btn primary t-label-medium"
            disabled={busy || (lost && free && !detail.trim())}
            onClick={() => void save()}
          >Save</button>
        </>
      }
    >
      <Field label="Stage">
        <select className="field" value={stage} onChange={(e) => setStage(e.target.value as LeadStage)}>
          {[...OPEN_PATH, 'won' as const, 'lost' as const].map((k) => (
            <option key={k} value={k}>{STAGES[k].long}</option>
          ))}
        </select>
      </Field>

      {/* Only for the one stage it applies to. Asking why a lead became a member is
          noise, and a form that asks it everywhere gets answered nowhere. */}
      {lost && (
        <>
          <Field
            label="Why did they not join?"
            hint="This is the field the whole screen exists for. It is required."
          >
            <select className="field" value={reason} onChange={(e) => setReason(e.target.value)}>
              {DROPOUT_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
              <option value={FREETEXT}>Something else…</option>
            </select>
          </Field>
          <Field label={free ? 'In their words' : 'Anything else worth keeping'}>
            <textarea
              className="field" rows={2} value={detail} autoFocus={free}
              placeholder={free ? 'What they actually said.' : 'Optional.'}
              onChange={(e) => setDetail(e.target.value)}
            />
          </Field>
        </>
      )}
    </Modal>
  );
}

export function BookTrialModal({ l, onClose, onDone }: {
  l: Lead; onClose: () => void; onDone: () => void;
}) {
  const source = useSource();
  const toast = useToast();
  const today = new Date().toISOString().slice(0, 10);
  const { data: classes } = useAsync(() => source.getClasses(today), [source, today]);
  const [classId, setClassId] = useState('');
  const [date, setDate] = useState(nextDays()[1] ?? today);
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!classId) { toast('Pick a session', 'bad'); return; }
    setBusy(true);
    try {
      await source.bookTrial(l.id, classId, date);
      toast(`Trial booked for ${l.firstName}`, 'good');
      onDone();
    } catch (e) {
      toast((e as Error).message, 'bad');
      setBusy(false);
    }
  }

  return (
    <Modal
      title={'Book a trial · ' + l.name} onClose={onClose}
      footer={
        <>
          <button type="button" className="btn t-label-medium" onClick={onClose}>Cancel</button>
          <button type="button" className="btn primary t-label-medium" disabled={busy}
            onClick={() => void save()}>Book it</button>
        </>
      }
    >
      <Field label="Session">
        <select className="field" value={classId} autoFocus onChange={(e) => setClassId(e.target.value)}>
          <option value="">Pick one</option>
          {(classes ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.time} · {c.name}{c.instructor ? ' · ' + c.instructor : ''}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Day">
        <select className="field" value={date} onChange={(e) => setDate(e.target.value)}>
          {nextDays().map((d) => (
            <option key={d} value={d}>
              {d === today ? 'Today' : new Date(d + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
            </option>
          ))}
        </select>
      </Field>
    </Modal>
  );
}

/** Today and the next six days. The timetable repeats weekly. */
function nextDays(): string[] {
  const out: string[] = [];
  const now = new Date();
  for (let i = 0; i < 7; i++) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i, 12);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
  }
  return out;
}
